import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { submitQuiz as submitQuizSync } from "@/lib/offlineSync";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CheckCircle, XCircle, Lightning, ArrowRight, Timer, Warning, Target } from "@phosphor-icons/react";
import { MASCOT } from "@/lib/assets";
import { Confetti } from "@/components/Confetti";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export default function Quiz() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const examMode = params.get("mode") === "exam";
  const durationMin = parseInt(params.get("duration") || "0", 10);

  const [quiz, setQuiz] = useState(null);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(examMode ? durationMin * 60 : 0);
  const startRef = useRef(Date.now());
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    api.get(`/quizzes/${encodeURIComponent(quizId)}`).then((r) => setQuiz(r.data)).catch(() => navigate("/app/cours"));
  }, [quizId, navigate]);

  const submit = useCallback(async () => {
    if (!quiz) return;
    setSubmitting(true);
    setElapsed(Math.round((Date.now() - startRef.current) / 1000));
    try {
      const arr = quiz.questions.map((_, i) => (answers[i] ?? -1));
      const { result, queued } = await submitQuizSync(quizId, { answers: arr, mode: examMode ? "blanc" : "apprentissage" });
      if (queued) {
        setResult({ score: 0, correct: 0, total: quiz.questions.length, xp_gained: 0,
          level: { level: 1, name: "" }, queued: true,
          results: quiz.questions.map((q) => ({ correct_index: q.answer, chosen: -1, ok: false, explanation: q.explanation })) });
      } else {
        setResult(result);
      }
    } finally { setSubmitting(false); }
  }, [quiz, answers, quizId]);

  // Countdown timer for exam mode
  useEffect(() => {
    if (!examMode || !quiz || result) return;
    if (timeLeft <= 0) { submit(); return; }
    const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [examMode, quiz, result, timeLeft, submit]);

  // Celebrate high scores with confetti
  const [confetti, setConfetti] = useState(false);
  useEffect(() => {
    if (result && result.score >= 80) {
      setConfetti(true);
      const t = setTimeout(() => setConfetti(false), 4500);
      return () => clearTimeout(t);
    }
  }, [result]);

  if (!quiz) return <div className="h-64 animate-pulse rounded-2xl bg-sand" />;

  if (result) {
    const wrong = quiz.questions.map((q, i) => ({ q, i, r: result.results[i] })).filter((x) => !x.r.ok);
    const advice = result.score >= 80
      ? "Excellent ! Vous maîtrisez cette matière. Poursuivez avec un sujet plus difficile."
      : result.score >= 50
        ? "Bon travail. Concentrez vos révisions sur les notions manquées ci-dessous."
        : "Ne lâchez rien. Reprenez le cours de cette matière puis retentez ce simulateur.";
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        {confetti && <Confetti />}
        {result.queued && (
          <div data-testid="offline-queued" className="rounded-2xl border border-gold/40 bg-gold/10 p-4 text-sm font-semibold text-[#8a6d1e]">
            Hors connexion : votre copie est enregistrée sur cet appareil et sera synchronisée automatiquement dès le retour du réseau.
          </div>
        )}
        <div className="relative mt-14 rounded-2xl border border-border bg-forest p-8 pt-16 text-center text-white animate-fade-up">
          <img data-testid="quiz-mascot" src={result.score >= 50 ? MASCOT.celebrate : MASCOT.encourage} alt=""
            className="absolute left-1/2 top-0 h-28 w-28 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-white bg-white object-contain shadow-xl" />
          <div className="font-display text-lg font-bold text-gold">
            {result.score >= 80 ? "Bravo, champion ! 🎉" : result.score >= 50 ? "Bien joué, continue !" : "Courage, tu vas y arriver !"}
          </div>
          <h1 className="mt-1 font-display text-4xl font-extrabold">{result.score}%</h1>
          <p className="text-white/80">{result.correct} / {result.total} bonnes réponses</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-sm font-semibold">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2">
              <Lightning weight="fill" className="text-gold" /> +{result.xp_gained} XP · Niveau {result.level.level} ({result.level.name})
            </span>
            {examMode && (
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2" data-testid="quiz-time-used">
                <Timer weight="fill" className="text-gold" /> {fmt(elapsed)} utilisées
              </span>
            )}
          </div>
        </div>

        {/* Error analysis */}
        <div className="rounded-2xl border border-border bg-white p-6 animate-fade-up" data-testid="error-analysis">
          <div className="flex items-center gap-2">
            <Target weight="fill" className="text-terracotta" size={22} />
            <h2 className="font-display text-xl font-bold">Analyse des erreurs</h2>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">{advice}</p>
          {wrong.length === 0 ? (
            <div className="mt-4 flex items-center gap-2 rounded-xl bg-forest/5 px-4 py-3 text-sm font-semibold text-forest">
              <CheckCircle weight="fill" /> Aucune erreur — sans faute !
            </div>
          ) : (
            <div className="mt-4 space-y-2">
              <div className="text-sm font-semibold text-destructive">{wrong.length} notion(s) à revoir :</div>
              {wrong.map(({ q, r, i }) => (
                <div key={i} data-testid={`error-item-${i}`} className="flex items-start gap-2 rounded-xl bg-destructive/5 px-4 py-3 text-sm">
                  <Warning weight="fill" className="mt-0.5 shrink-0 text-destructive" size={18} />
                  <div>
                    <div className="font-medium">{q.q}</div>
                    <div className="mt-1 text-forest">Bonne réponse : {q.options[r.correct_index]}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-3">
          {quiz.questions.map((q, i) => {
            const r = result.results[i];
            return (
              <div key={i} className="rounded-2xl border border-border bg-white p-5">
                <div className="flex items-start gap-2">
                  {r.ok ? <CheckCircle weight="fill" className="mt-0.5 shrink-0 text-forest" size={20} /> : <XCircle weight="fill" className="mt-0.5 shrink-0 text-destructive" size={20} />}
                  <div className="flex-1">
                    <div className="font-semibold">{q.q}</div>
                    <div className="mt-1 text-sm text-forest">Bonne réponse : {q.options[r.correct_index]}</div>
                    <div className="mt-1 text-sm text-muted-foreground">{r.explanation}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-3">
          <Button onClick={() => navigate(examMode ? "/app/simulateur" : "/app/cours")} variant="outline" className="flex-1 rounded-full">{examMode ? "Autres simulateurs" : "Retour aux cours"}</Button>
          <Button data-testid="quiz-retry" onClick={() => { setResult(null); setAnswers({}); setCurrent(0); setTimeLeft(examMode ? durationMin * 60 : 0); startRef.current = Date.now(); }} className="flex-1 rounded-full bg-terracotta text-white hover:bg-[#E03E00]">Recommencer</Button>
        </div>
      </div>
    );
  }

  const q = quiz.questions[current];
  const answered = Object.keys(answers).length;
  const isLast = current === quiz.questions.length - 1;
  const lowTime = examMode && timeLeft <= 30;
  const chosen = answers[current];
  const revealed = !examMode && chosen !== undefined;
  const isCorrect = revealed && chosen === q.answer;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-terracotta"><ArrowLeft /> Quitter</button>
        {examMode && (
          <div data-testid="quiz-timer" className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-bold tabular-nums transition-colors ${lowTime ? "bg-destructive text-white animate-pulse" : "bg-forest text-white"}`}>
            <Timer weight="fill" size={16} /> {fmt(timeLeft)}
          </div>
        )}
      </div>
      <div>
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span className="font-semibold">{examMode ? "Examen blanc — " : ""}{quiz.title}</span>
          <span>Question {current + 1} / {quiz.questions.length}</span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-sand">
          <div className="h-full rounded-full bg-terracotta transition-all" style={{ width: `${((current + 1) / quiz.questions.length) * 100}%` }} />
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-white p-6 animate-fade-up">
        <h2 className="font-display text-xl font-bold">{q.q}</h2>
        <div className="mt-5 space-y-3">
          {q.options.map((opt, i) => {
            const sel = chosen === i;
            let cls = sel ? "border-terracotta bg-terracotta/5" : "border-border hover:border-terracotta/40";
            let badge = sel ? "border-terracotta bg-terracotta text-white" : "border-border";
            let mark = String.fromCharCode(65 + i);
            if (revealed) {
              if (i === q.answer) { cls = "border-green-500 bg-green-50"; badge = "border-green-500 bg-green-500 text-white"; mark = "✓"; }
              else if (i === chosen) { cls = "border-destructive bg-destructive/5"; badge = "border-destructive bg-destructive text-white"; mark = "✗"; }
              else { cls = "border-border opacity-50"; }
            }
            return (
              <button key={i} data-testid={`quiz-option-${i}`} disabled={revealed}
                onClick={() => { if (revealed) return; setAnswers({ ...answers, [current]: i }); }}
                className={`flex w-full items-center gap-3 rounded-xl border-2 px-4 py-3 text-left text-sm transition ${cls}`}>
                <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 text-xs font-bold ${badge}`}>{mark}</span>
                {opt}
              </button>
            );
          })}
        </div>
        {revealed && (
          <div data-testid="quiz-feedback" className={`mt-5 flex items-start gap-3 rounded-2xl border p-4 ${isCorrect ? "border-green-200 bg-green-50" : "border-terracotta/20 bg-terracotta/5"}`}>
            <img src={isCorrect ? MASCOT.celebrate : MASCOT.encourage} alt="" className="h-16 w-16 shrink-0 object-contain" />
            <div>
              <div className={`font-display font-bold ${isCorrect ? "text-green-700" : "text-terracotta"}`}>{isCorrect ? "Bravo, bonne réponse ! 🎉" : "Pas grave, on retient !"}</div>
              {!isCorrect && <div className="mt-0.5 text-sm font-semibold text-forest">Bonne réponse : {q.options[q.answer]}</div>}
              {q.explanation && <div className="mt-1 text-sm text-muted-foreground">{q.explanation}</div>}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <Button variant="outline" disabled={current === 0} onClick={() => setCurrent(current - 1)} className="rounded-full">Précédent</Button>
        {isLast ? (
          <Button data-testid="quiz-submit" disabled={(examMode ? answered === 0 : answered < quiz.questions.length) || submitting} onClick={submit} className="rounded-full bg-forest text-white hover:bg-forest/90">
            {submitting ? "Correction…" : examMode ? "Remettre l'examen" : "Terminer le quiz"}
          </Button>
        ) : (
          <Button data-testid="quiz-next" disabled={answers[current] === undefined} onClick={() => setCurrent(current + 1)} className="rounded-full bg-terracotta text-white hover:bg-[#E03E00]">
            Suivant <ArrowRight weight="bold" className="ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}
